/**
 * Document Load Queue Manager
 * Manages concurrent document loading to prevent performance issues
 * when many documents are added to the board simultaneously
 */

type QueueItem<T> = {
  id: string
  task: () => Promise<T>
  resolve: (value: T) => void
  reject: (error: unknown) => void
}

class DocumentLoadQueue {
  private queue: QueueItem<any>[] = []
  private activeCount = 0
  private maxConcurrent: number

  constructor(maxConcurrent = 3) {
    this.maxConcurrent = maxConcurrent
  }

  /**
   * Add a document load task to the queue
   * @param id - Unique identifier for the task
   * @param task - Async function that loads the document
   * @returns Promise that resolves when the document is loaded
   */
  async add<T>(id: string, task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.queue.push({ id, task, resolve, reject })
      this.processQueue()
    })
  }

  private async processQueue() {
    // If we're at max concurrent tasks or queue is empty, do nothing
    if (this.activeCount >= this.maxConcurrent || this.queue.length === 0) {
      return
    }

    // Get next item from queue
    const item = this.queue.shift()
    if (!item) return

    this.activeCount++

    try {
      const result = await item.task()
      item.resolve(result)
    } catch (error) {
      item.reject(error)
    } finally {
      this.activeCount--
      // Process next item in queue
      this.processQueue()
    }
  }

  /**
   * Get the current queue size
   */
  getQueueSize(): number {
    return this.queue.length
  }

  /**
   * Get the number of active tasks
   */
  getActiveCount(): number {
    return this.activeCount
  }

  /**
   * Update the maximum concurrent tasks
   */
  setMaxConcurrent(max: number) {
    this.maxConcurrent = max
    // Process queue in case we increased the limit
    this.processQueue()
  }
}

// Export class for testing
export { DocumentLoadQueue }

// Export singleton instance with 6 concurrent loads
// (boards show up to 10 documents; 6 concurrency loads them in ~2 batches instead of ~4)
export const documentLoadQueue = new DocumentLoadQueue(6)
